/**
 * SignalR Transport Implementation
 *
 * Connects to ASP.NET SignalR hubs for real-time bidirectional communication.
 * Handles automatic reconnection with exponential backoff.
 */

import * as SignalR from '@microsoft/signalr';
import { BaseTransport } from './BaseTransport';
import type { TransportConfig } from '../types';

export class SignalRTransport extends BaseTransport {
  private connection: SignalR.HubConnection | null = null;

  private messageMethodName = 'ReceiveMessage';

  constructor(id: string, config: TransportConfig) {
    super(id, 'signalr', config);
  }

  async connect(): Promise<void> {
    if (this.connection?.state === SignalR.HubConnectionState.Connected) {
      return;
    }

    this.setStatus('connecting');

    try {
      const builder = new SignalR.HubConnectionBuilder()
        .withUrl(this.config.url, this.config.accessToken
          ? { accessTokenFactory: () => this.config.accessToken! }
          : {})
        .withAutomaticReconnect();

      if (this.config.headers) {
        builder.withAutomaticReconnect();
      }

      this.connection = builder.build();

      // Register message handler
      this.connection.on(this.messageMethodName, (raw: string | ArrayBuffer) => {
        this.emitMessage(raw);
      });

      // Register reconnection handlers
      this.connection.onreconnecting(() => {
        this.setStatus('reconnecting');
      });

      this.connection.onreconnected(() => {
        this.setStatus('connected');
      });

      this.connection.onclose(() => {
        this.setStatus('disconnected');
      });

      await this.connection.start();
      this.setStatus('connected');
    } catch (error) {
      this.setStatus('error');
      this.emitError(error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  }

  async disconnect(): Promise<void> {
    if (this.connection) {
      try {
        await this.connection.stop();
      } catch (error) {
        this.logger.error('Error stopping SignalR connection', error as Error);
      }
      this.connection = null;
    }
    this.setStatus('disconnected');
    this.cleanup();
  }

  async send(data: string | ArrayBuffer): Promise<void> {
    if (this.connection?.state !== SignalR.HubConnectionState.Connected) {
      throw new Error('SignalR connection not connected');
    }

    try {
      const message = typeof data === 'string' ? data : new TextDecoder().decode(new Uint8Array(data));
      await this.connection.invoke('SendMessage', message);
    } catch (error) {
      this.emitError(error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  }
}
